import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
  Row,
  Col,
  Table,
  Tag,
  Card,
  Tabs,
  Modal,
  Descriptions,
  Badge,
  Spin,
  Popconfirm,
  Tooltip,
  Typography,
  Alert,
  Skeleton,
  Timeline,
  Popover
} from 'antd';
import {
  PlusOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CalendarOutlined,
  ClearOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CreditCardOutlined,
  RiseOutlined,
  HistoryOutlined,
  CheckOutlined
} from '@ant-design/icons';
import API from '../../API/API';
import ErrorBoundary from '../../Components/ErrorBoundary';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(quarterOfYear); // needed for .startOf('quarter')/.endOf('quarter') below — not in dayjs core
dayjs.extend(utc);
dayjs.extend(timezone); // .tz() — every date on this page is shown and entered in Pacific

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;

// ========== PACIFIC TIME ==========
// Instants are stored in UTC; this page reads and writes them all in Pacific, so
// an agent in any timezone sees the same wall clock as the business does.
const PST_TZ = 'America/Los_Angeles';
const PST_FORMAT = 'YYYY-MM-DD HH:mm';
const PST_FORMAT_SECONDS = 'YYYY-MM-DD HH:mm:ss';
// The wall-clock string the picker round-trip below is built on — seconds
// included so a time the user set isn't silently truncated on the way back.
const WALL_CLOCK = 'YYYY-MM-DDTHH:mm:ss';

const formatPST = (value, format = PST_FORMAT) =>
  value ? dayjs(value).tz(PST_TZ).format(format) : 'N/A';

// antd's DatePicker renders and manipulates whatever dayjs object it's handed in
// the browser's own timezone, so handing it a value carrying a fixed Pacific
// offset falls apart the moment the user opens the panel. These convert at the
// boundary instead: into the picker an instant becomes a plain local dayjs whose
// wall clock reads as Pacific, and out of it that wall clock is re-anchored to
// Pacific to recover the real instant. Everything in between is ordinary dayjs.
const toPickerValue = (value) =>
  value ? dayjs(dayjs(value).tz(PST_TZ).format(WALL_CLOCK)) : null;

const fromPickerValue = (value) =>
  value ? dayjs.tz(value.format(WALL_CLOCK), PST_TZ).toISOString() : null;

// "Now", expressed the way the pickers above want it.
const nowInPST = () => toPickerValue(dayjs());

// Every column's width, in one place. Without explicit widths antd shares out
// whatever is left of scroll.x between the columns that lack one, which is what
// pushed the headers into each other — "Dispute Created Date" was rendering on
// top of "SLA Deadline". Each value is sized to the widest thing the column
// actually holds: a timestamp plus its History link, a status tag plus the edit
// pencil, and so on.
//
// The table's scroll width is derived from these (see totalColumnWidth), so
// adding or resizing a column can't leave the two out of step.
const COLUMN_WIDTH = {
  selection: 48,
  leadInfo: 300,
  financial: 320,
  disputeReason: 190,
  status: 150,
  // 20 characters of header plus a sort control — the longest title in the table.
  disputeDate: 210,
  slaDeadline: 175,
  comments: 210,
  actions: 165,
  // The Deleted tab carries Restore and Delete Permanently alongside View and
  // Edit, so its action buttons need room to sit on two lines.
  actionsDeleted: 300
};

// Columns are laid out with table-layout: fixed once scroll.x is set, so the
// scroll width has to be the sum of the columns — short and they get squeezed
// back together, long and the last column stretches into dead space.
const totalColumnWidth = (columns) =>
  columns.reduce((sum, column) => sum + (column.width || 0), COLUMN_WIDTH.selection);

// Free-text cells wrap inside their column instead of forcing it wider; the
// break-word covers a long unbroken string, which would otherwise spill out.
const wrappedCellStyle = { whiteSpace: 'normal', wordBreak: 'break-word' };

// The Dispute Created Date column and its picker read dispute_date, falling back
// to created_at for documents written before that field existed.
const getDisputeDate = (record) =>
  record?.dispute_date || record?.created_at || record?.createdAt || null;

// The Deleted tab's cells (LeadInfoCell, FinancialCell) read a flat lead shape
// that fetchLeadDetails used to build from a per-row request. The deleted list
// now populates those same fields on each row, so the shape is derived here
// instead — no network, identical output.
//
// Deliberately NOT written into leadDetailsCache: that cache also feeds the
// detail modal, which reads shippingAddress, billingAddress and paidAmount —
// fields the list's projection doesn't carry. Seeding it from here would turn a
// cache miss the modal handles into a hit that silently renders them blank.
const leadDetailsFromRow = (leadIdObj) => {
  // Unpopulated rows come back as a bare id (or nothing at all when the lead was
  // hard-deleted); only an object carries the fields below.
  if (!leadIdObj || typeof leadIdObj !== 'object' || !leadIdObj.personalInfo) return null;

  return {
    fullName: leadIdObj.personalInfo?.fullName || 'N/A',
    email: leadIdObj.personalInfo?.email || 'N/A',
    phoneNumber: leadIdObj.personalInfo?.phoneNumber || 'N/A',
    zipCode: leadIdObj.personalInfo?.zipCode || 'N/A',
    customerTimeZone: leadIdObj.personalInfo?.customerTimeZone || 'N/A',
    orderNumber: leadIdObj.orderNumber || 'N/A',
    leadStage: leadIdObj.leadStage || 'N/A',
    status: leadIdObj.status || 'N/A',
    vehicleInfo: {
      make: leadIdObj.vehicleInfo?.make || 'N/A',
      model: leadIdObj.vehicleInfo?.model || 'N/A',
      year: leadIdObj.vehicleInfo?.year || 'N/A',
      part: leadIdObj.vehicleInfo?.part || 'N/A',
      vin: leadIdObj.vehicleInfo?.vin || 'N/A'
    },
    saleMadeCharge: {
      customerAmountReceived: leadIdObj.saleMadeCharge?.customerAmountReceived || 0,
      yardPrice: leadIdObj.saleMadeCharge?.yardPrice || 0,
      customerCCFee: leadIdObj.saleMadeCharge?.customerCCFee || 0,
      totalGP: leadIdObj.saleMadeCharge?.totalGP || 0
    },
    yard: leadIdObj.yard || [],
    advancePayments: leadIdObj.advancePayments || [],
    totalAmount: leadIdObj.totalAmount || 0,
    currentAgentName: leadIdObj.handledBy?.firstName && leadIdObj.handledBy?.lastName
      ? `${leadIdObj.handledBy.firstName} ${leadIdObj.handledBy.lastName}`
      : leadIdObj.handledBy?.name || 'N/A',
    handledBy: leadIdObj.handledBy || 'N/A'
  };
};

// Module-scope so these aren't reallocated on every render, and so the
// hoisted <SearchAndFilters> component (below DisputeLead) can read them too.
const MIN_SEARCH_CHARS = 3;

// Pause after the last keystroke before the Create Dispute drawer's lead lookup
// goes to the server. Long enough that a typed word is one request, short enough
// that the dropdown still feels immediate.
const LEAD_SEARCH_DEBOUNCE_MS = 300;

// How many leads the drawer's dropdown asks for at a time. The server matches the
// typed term across name, email, phone and order number, so a short list plus a
// search beats a long list nobody would scroll: whatever the user is looking for
// is one or two keystrokes away.
const LEAD_SEARCH_LIMIT = 50;

// Only what the dropdown actually renders — the option label is built from the
// name, order number and email — plus handledBy, which each option carries as
// owner_id. Without this the endpoint returns whole lead documents, roughly
// 3.8 KB each against 134 B for these.
const LEAD_SEARCH_FIELDS = [
  'personalInfo.fullName',
  'personalInfo.email',
  'personalInfo.phoneNumber',
  'orderNumber',
  'handledBy'
].join(',');

const quickFilterOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisQuarter', label: 'This Quarter' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'lastYear', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

// Matches the Dispute schema's status enum exactly (DisputeSchema.js) — 'ALL'
// is a frontend-only sentinel meaning "don't send a status param at all".
const statusFilterOptions = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'LOSS', label: 'Loss' },
];

// Module-scope (not nested inside DisputeLead): a component declared inside another
// component's render body is a brand-new component type on every render, which forces
// React to unmount/remount it — dropping input focus and any local state each time.
// That was causing the search box to lose focus / reset while the parent re-rendered
// during data loads. Declaring it here once fixes that; state that used to live inside
// it (`localSearchValue`) now lives in DisputeLead and is passed down as a prop.
const SearchAndFilters = ({
  localSearchValue,
  onLocalSearchChange,
  onExecuteSearch,
  searchText,
  quickFilter,
  onQuickFilterChange,
  showCustomRange,
  filterDateRange,
  onCustomRangeChange,
  onResetFilters,
  onClearSearch,
  onClearCustomDate,
  statusFilter,
  onStatusFilterChange,
}) => {
  return (
    <Card
      size="small"
      style={{ marginBottom: 16, background: '#fafafa' }}
      bodyStyle={{ padding: '12px' }}
    >
      <Row gutter={[16, 12]}>
        <Col xs={24} md={8}>
          <div>
            <Input.Search
              placeholder={`Search by lead name, email, order number, phone, or dispute details... (min. ${MIN_SEARCH_CHARS} characters)`}
              allowClear
              value={localSearchValue}
              onChange={(e) => {
                const value = e.target.value;
                onLocalSearchChange(value);
                // antd's allowClear (×) fires onChange with '' — without this, only
                // localSearchValue cleared and the actual search (searchText, which
                // drives the query and the "Search active" banner) stayed applied.
                if (value === '') {
                  onExecuteSearch('');
                }
              }}
              onSearch={(value) => onExecuteSearch(value)}
              onPressEnter={(e) => onExecuteSearch(e.target.value)}
              prefix={<span style={{ color: '#bfbfbf' }}>🔍</span>}
              enterButton
            />

            {searchText && searchText.length >= MIN_SEARCH_CHARS && (
              <div style={{ fontSize: '12px', color: '#4caf50', marginTop: '4px' }}>
                ✓ Search active for: "{searchText}"
              </div>
            )}
          </div>
        </Col>

        <Col xs={24} md={8}>
          <Select
            placeholder="Quick Date Filter"
            style={{ width: '100%' }}
            value={quickFilter}
            onChange={onQuickFilterChange}
            suffixIcon={<CalendarOutlined />}
            options={quickFilterOptions}
          />
        </Col>

        <Col xs={24} md={8}>
          <Select
            placeholder="Status"
            style={{ width: '100%' }}
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={statusFilterOptions}
          />
        </Col>

        <Col xs={24} md={8}>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            placeholder={['Start Date', 'End Date']}
            value={filterDateRange}
            onChange={onCustomRangeChange}
            format="YYYY-MM-DD"
          />
        </Col>

        <Col xs={24} md={showCustomRange ? 24 : 8}>
          <Button
            onClick={onResetFilters}
            icon={<ClearOutlined />}
            style={{ width: '100%' }}
          >
            Reset All Filters
          </Button>
        </Col>
      </Row>

      {(searchText || quickFilter !== 'thisMonth' || statusFilter !== 'ALL') && (
        <div style={{ marginTop: 12 }}>
          <Space size={[0, 8]} wrap>
            <span style={{ color: '#666', marginRight: 8 }}>Active Filters:</span>
            {searchText && (
              <Tag
                closable
                onClose={onClearSearch}
                color="blue"
              >
                Search: {searchText}
              </Tag>
            )}

            {statusFilter !== 'ALL' && (
              <Tag
                closable
                onClose={() => onStatusFilterChange('ALL')}
                color="geekblue"
              >
                Status: {statusFilterOptions.find(opt => opt.value === statusFilter)?.label}
              </Tag>
            )}

            {quickFilter !== 'thisMonth' && quickFilter !== 'custom' && (
              <Tag
                closable
                onClose={() => onQuickFilterChange('thisMonth')}
                color="purple"
              >
                Date: {quickFilterOptions.find(opt => opt.value === quickFilter)?.label}
              </Tag>
            )}
            {quickFilter === 'custom' && filterDateRange && (
              <Tag
                closable
                onClose={onClearCustomDate}
                color="purple"
              >
                Date: {dayjs(filterDateRange[0]).format('YYYY-MM-DD')} to {dayjs(filterDateRange[1]).format('YYYY-MM-DD')}
              </Tag>
            )}
          </Space>
        </div>
      )}
    </Card>
  );
};

// Shared style objects for the per-row cell components below — module-scope so
// they're allocated once at load instead of once per row on every table render.
// Only the styles that are IDENTICAL on every row are here; anything that varies
// by row data (colors driven by status/amount, etc.) stays inline where it's used.
const cellStyles = {
  avatarCircle: {
    width: 32, height: 32, borderRadius: 16,
    background: '#1890ff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 'bold'
  },
  leadInfoContainer: { display: 'flex', flexDirection: 'column', gap: 12, minWidth: 260 },
  leadNameRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  leadNameStrong: { fontSize: 13, display: 'block' },
  leadOrderSmall: { color: '#52c41a', fontSize: 11 },
  leadContactBlock: { paddingLeft: 40 },
  leadContactRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  leadContactIcon: { fontSize: 11, color: '#666' },
  leadContactValue: { fontSize: 11 },
  vehicleBlock: { borderTop: '1px solid #f0f0f0', paddingTop: 8, paddingLeft: 40 },
  vehicleRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 },
  vehicleTag: { fontSize: 10, margin: 0 },
  vehicleText: { fontSize: 12, fontWeight: 500 },
  vehicleVin: { fontSize: 10, color: '#999', marginTop: 4 },
  financialCard: {
    background: '#fafafa', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #f0f0f0', position: 'relative'
  },
  financialRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  financialLabel: { fontSize: 11, color: '#666' },
  financialFeeBadge: {
    position: 'absolute', top: 8, right: 8,
    fontSize: 10, padding: '0 6px', margin: 0, borderRadius: 10
  },
  financialGpBlock: { borderTop: '1px dashed #e8e8e8', marginTop: 4, paddingTop: 8 },
  financialMarginBlock: { marginTop: 8, paddingTop: 6, borderTop: '1px dashed #e8e8e8', textAlign: 'center' },
  financialPaymentRow: {
    marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 4, fontSize: 9, color: '#999'
  },
};

// Active-disputes table only: reads lead_id directly off the row instead of
// leadDetailsCache — getAllActiveDisputes returns lead_id fully populated (see
// LEAD_LIST_FIELDS backend-side), so there's no per-row fetch to wait on.
// React.memo + a stable prop (record) means a row that hasn't changed skips
// re-rendering even when other rows' loading state or the table's pagination
// state changes.
const LeadInfoCellPopulated = React.memo(({ record }) => {
  const lead = record.lead_id;

  if (!lead || typeof lead !== 'object') {
    return (
      <div>
        <strong>Lead ID: {record.lead_id || 'N/A'}</strong>
        <div><small style={{ color: '#999' }}>Lead details unavailable</small></div>
      </div>
    );
  }

  const fullName = lead.personalInfo?.fullName || 'N/A';
  const email = lead.personalInfo?.email || 'N/A';
  const phoneNumber = lead.personalInfo?.phoneNumber || 'N/A';
  const zipCode = lead.personalInfo?.zipCode || 'N/A';
  const vehicleInfo = lead.vehicleInfo || {};
  const hasVehicleInfo = !!vehicleInfo.make;

  return (
    <div style={cellStyles.leadInfoContainer}>
      <div>
        <div style={cellStyles.leadNameRow}>
          <div style={cellStyles.avatarCircle}>
            {fullName?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <strong style={cellStyles.leadNameStrong}>{fullName}</strong>
            <small style={cellStyles.leadOrderSmall}>Order: {lead.orderNumber || 'N/A'}</small>
          </div>
        </div>

        <div style={cellStyles.leadContactBlock}>
          <div style={cellStyles.leadContactRow}>
            <span style={cellStyles.leadContactIcon}>📧</span>
            <span style={cellStyles.leadContactValue}>{email}</span>
          </div>
          <div style={cellStyles.leadContactRow}>
            <span style={cellStyles.leadContactIcon}>📞</span>
            <span style={cellStyles.leadContactValue}>{phoneNumber}</span>
          </div>
          {zipCode && zipCode !== 'N/A' && (
            <div style={cellStyles.leadContactRow}>
              <span style={cellStyles.leadContactIcon}>📍</span>
              <span style={cellStyles.leadContactValue}>Zip: {zipCode}</span>
            </div>
          )}
        </div>
      </div>

      {hasVehicleInfo && (
        <div style={cellStyles.vehicleBlock}>
          <div style={cellStyles.vehicleRow}>
            <Tag color="blue" style={cellStyles.vehicleTag}>{vehicleInfo.year}</Tag>
            <span style={cellStyles.vehicleText}>
              {vehicleInfo.make} {vehicleInfo.model} - {vehicleInfo.part}
            </span>
          </div>
          {vehicleInfo.vin && (
            <div style={cellStyles.vehicleVin}>VIN: {vehicleInfo.vin}</div>
          )}
        </div>
      )}
    </div>
  );
});
LeadInfoCellPopulated.displayName = 'LeadInfoCellPopulated';

// Deleted-tab only: leadDetails/isLoading are computed by the caller from
// leadDetailsCache/loadingLeadDetails (module-scope components can't reach those
// closures directly) and passed down as props, so memo can compare them per row.
const LeadInfoCell = React.memo(({ leadId, leadDetails, isLoading }) => {
  if (isLoading) {
    return <Spin size="small" />;
  }

  if (!leadDetails) {
    return (
      <div>
        <strong>Lead ID: {leadId || 'N/A'}</strong>
        <div><small style={{ color: '#999' }}>Click to load details</small></div>
      </div>
    );
  }

  return (
    <div style={cellStyles.leadInfoContainer}>
      <div>
        <div style={cellStyles.leadNameRow}>
          <div style={cellStyles.avatarCircle}>
            {leadDetails.fullName?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <strong style={cellStyles.leadNameStrong}>{leadDetails.fullName}</strong>
            <small style={cellStyles.leadOrderSmall}>Order: {leadDetails.orderNumber}</small>
          </div>
        </div>

        <div style={cellStyles.leadContactBlock}>
          <div style={cellStyles.leadContactRow}>
            <span style={cellStyles.leadContactIcon}>📧</span>
            <span style={cellStyles.leadContactValue}>{leadDetails.email}</span>
          </div>
          <div style={cellStyles.leadContactRow}>
            <span style={cellStyles.leadContactIcon}>📞</span>
            <span style={cellStyles.leadContactValue}>{leadDetails.phoneNumber}</span>
          </div>
          {leadDetails.zipCode && leadDetails.zipCode !== 'N/A' && (
            <div style={cellStyles.leadContactRow}>
              <span style={cellStyles.leadContactIcon}>📍</span>
              <span style={cellStyles.leadContactValue}>Zip: {leadDetails.zipCode}</span>
            </div>
          )}
        </div>
      </div>

      {leadDetails.vehicleInfo && leadDetails.vehicleInfo.make !== 'N/A' && (
        <div style={cellStyles.vehicleBlock}>
          <div style={cellStyles.vehicleRow}>
            <Tag color="blue" style={cellStyles.vehicleTag}>{leadDetails.vehicleInfo.year}</Tag>
            <span style={cellStyles.vehicleText}>
              {leadDetails.vehicleInfo.make} {leadDetails.vehicleInfo.model} - {leadDetails.vehicleInfo.part}
            </span>
          </div>
          {leadDetails.vehicleInfo.vin && leadDetails.vehicleInfo.vin !== 'N/A' && (
            <div style={cellStyles.vehicleVin}>VIN: {leadDetails.vehicleInfo.vin}</div>
          )}
        </div>
      )}
    </div>
  );
});
LeadInfoCell.displayName = 'LeadInfoCell';

// Deleted-tab FINANCIAL column — same leadDetails-as-prop pattern as LeadInfoCell.
const FinancialCell = React.memo(({ leadDetails }) => {
  const sale = leadDetails?.saleMadeCharge || {};
  const totalAmount = Number(leadDetails?.totalAmount || 0);
  const customerAmountReceived = sale.customerAmountReceived || totalAmount || 0;
  const customerCCFee = sale.customerCCFee || 0;
  const totalGP = sale.totalGP || 0;

  const yards = leadDetails?.yard || [];
  const currentYard = yards.find((yard) => yard.isCurrent === true) || yards[yards.length - 1];
  const yardPrice = currentYard?.finalPrice || 0;

  const lastPayment = leadDetails?.advancePayments?.length
    ? leadDetails.advancePayments[leadDetails.advancePayments.length - 1]
    : null;
  const paymentMethod = lastPayment?.paymentMethod || null;

  const getFeeBadge = () => {
    const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
    if (threePointFivePercent.includes(paymentMethod)) return '3.5%';
    if (paymentMethod === 'Pay Tomorrow') return '6%';
    if (paymentMethod === 'Wire Transfer') return '$35';
    return paymentMethod ? '0%' : null;
  };
  const feeBadge = getFeeBadge();

  const margin = customerAmountReceived > 0
    ? ((totalGP / customerAmountReceived) * 100).toFixed(1)
    : '0.0';
  const isGPPositive = totalGP >= 0;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  if (Object.keys(sale).length === 0 && customerAmountReceived === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '8px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>No financial data</Text>
      </div>
    );
  }

  return (
    <div style={cellStyles.financialCard}>
      {feeBadge && (
        <Tag
          color={paymentMethod === 'Wire Transfer' ? 'orange' : 'purple'}
          style={cellStyles.financialFeeBadge}
        >
          {feeBadge}
        </Tag>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={cellStyles.financialRow}>
          <span style={cellStyles.financialLabel}>
            <DollarOutlined style={{ marginRight: 4, color: '#52c41a' }} />
            Amount Received:
          </span>
          <strong style={{ fontSize: 14, color: '#7c3aed' }}>{formatCurrency(customerAmountReceived)}</strong>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={cellStyles.financialRow}>
          <span style={cellStyles.financialLabel}>
            <ShoppingOutlined style={{ marginRight: 4, color: '#722ed1' }} />
            Yard Price:
          </span>
          <strong style={{ fontSize: 13, color: '#dc2626' }}>{formatCurrency(yardPrice)}</strong>
        </div>
        {yards.length > 0 && (
          <div style={{ fontSize: 9, color: '#999', textAlign: 'right', marginTop: 2 }}>(from current yard)</div>
        )}
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={cellStyles.financialRow}>
          <span style={cellStyles.financialLabel}>
            <CreditCardOutlined style={{ marginRight: 4, color: '#d97706' }} />
            CC Fee:
          </span>
          <span style={{ fontSize: 13, color: '#d97706', fontWeight: 500 }}>{formatCurrency(customerCCFee)}</span>
        </div>
      </div>

      <div style={cellStyles.financialGpBlock}>
        <div style={cellStyles.financialRow}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            <RiseOutlined style={{ marginRight: 4, color: isGPPositive ? '#059669' : '#dc2626' }} />
            Total GP:
          </span>
          <strong style={{ fontSize: 15, color: isGPPositive ? '#059669' : '#dc2626', fontWeight: 'bold' }}>
            {formatCurrency(totalGP)}
          </strong>
        </div>
      </div>

      {customerAmountReceived > 0 && (
        <div style={cellStyles.financialMarginBlock}>
          <span style={{ fontSize: 10, color: '#666' }}>
            Margin: <strong style={{ color: isGPPositive ? '#52c41a' : '#ff4d4f' }}>{margin}%</strong>
          </span>
          <Tooltip title={`GP: ${formatCurrency(totalGP)} / Amount: ${formatCurrency(customerAmountReceived)}`}>
            <span style={{ marginLeft: 4, fontSize: 10, color: '#999', cursor: 'help' }}>ⓘ</span>
          </Tooltip>
        </div>
      )}

      {paymentMethod && (
        <div style={cellStyles.financialPaymentRow}>
          <CreditCardOutlined style={{ fontSize: 10 }} />
          <span>{paymentMethod}</span>
        </div>
      )}
    </div>
  );
});
FinancialCell.displayName = 'FinancialCell';

const formatStatusLabel = (status) => status ? status.replace(/_/g, ' ') : 'N/A';

const formatChangedByName = (user) => {
  if (!user) return 'Unknown';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.email || 'Unknown';
};

// Not memoized — unlike the per-row cell components above, this only ever
// renders once (while the modal is open), not once per table row.
const StatusHistoryModal = ({ visible, onClose, loading, history, record }) => {
  const timelineItems = (history || []).map((entry, index) => ({
    key: entry._id || index,
    color: entry.status_from ? 'blue' : 'green',
    children: (
      <div>
        <strong>
          {entry.status_from
            ? `${formatStatusLabel(entry.status_from)} → ${formatStatusLabel(entry.status_to)}`
            : `Created — Status: ${formatStatusLabel(entry.status_to)}`}
        </strong>
        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
          by {formatChangedByName(entry.changed_by)}
          {entry.changed_at ? ` on ${formatPST(entry.changed_at)} PST` : ''}
        </div>
      </div>
    ),
  }));

  return (
    <Modal
      title="Dispute Status History"
      open={visible}
      onCancel={onClose}
      footer={[<Button key="close" onClick={onClose}>Close</Button>]}
      width={600}
    >
      {record?.dispute_reason && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6, fontSize: 13 }}>
          <strong>Dispute:</strong> {record.dispute_reason}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : timelineItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No status history recorded for this dispute.
        </div>
      ) : (
        <Timeline items={timelineItems} />
      )}
    </Modal>
  );
};

// Per-field wording for FieldHistoryModal below. Keys match the :field segment
// of GET /api/disputes/:id/field-history/:field — dispute_reason and description
// have their own logs server-side; `status` keeps its older status_from/status_to
// entry shape and its own modal.
const FIELD_HISTORY_CONFIG = {
  dispute_date: { title: 'Dispute Created Date History', noun: 'date', isDate: true },
  dispute_reason: { title: 'Dispute Reason History', noun: 'reason' },
  description: { title: 'Comments History', noun: 'comment' },
};

// Renders one append-only value log (see valueHistoryEntry in DisputeSchema).
// Not memoized, for the same reason StatusHistoryModal isn't: one instance, open
// or closed, rather than one per table row.
const FieldHistoryModal = ({ visible, field, onClose, loading, history, record }) => {
  const config = FIELD_HISTORY_CONFIG[field] || { title: 'History', noun: 'value' };

  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <Text type="secondary">(empty)</Text>;
    }
    if (config.isDate) return formatPST(value);
    return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(value)}</span>;
  };

  // Oldest first: these logs are appended to, so their natural order already reads
  // chronologically — no sort, which would also reorder same-timestamp entries.
  const timelineItems = (history || []).map((entry, index) => ({
    key: entry._id || index,
    color: entry.value_from === null || entry.value_from === undefined ? 'green' : 'blue',
    children: (
      <div>
        <div style={{ fontWeight: 500 }}>
          {entry.value_from === null || entry.value_from === undefined
            ? <>Created — {renderValue(entry.value_to)}</>
            : renderValue(entry.value_to)}
        </div>

        {entry.value_from !== null && entry.value_from !== undefined && (
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
            previously: {renderValue(entry.value_from)}
          </div>
        )}

        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          by {formatChangedByName(entry.changed_by)}
          {entry.changed_at ? ` on ${formatPST(entry.changed_at)} PST` : ''}
        </div>
      </div>
    ),
  }));

  return (
    <Modal
      title={config.title}
      open={visible}
      onCancel={onClose}
      footer={[<Button key="close" onClick={onClose}>Close</Button>]}
      width={600}
    >
      {record?.dispute_reason && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6, fontSize: 13 }}>
          <strong>Dispute:</strong> {record.dispute_reason}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : timelineItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No {config.noun} history recorded for this dispute.
        </div>
      ) : (
        <Timeline items={timelineItems} />
      )}
    </Modal>
  );
};

// The small "History" link that sits under a cell's value in the table.
const HistoryLink = ({ onClick }) => (
  <Button
    type="link"
    size="small"
    icon={<HistoryOutlined />}
    onClick={onClick}
    style={{ padding: 0, height: 'auto', fontSize: 11 }}
  >
    History
  </Button>
);

const DisputeLead = () => {
  // ========== UI STATE ==========
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  // ========== LOADING STATES ==========
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [permanentDeleteLoading, setPermanentDeleteLoading] = useState(false);
  const [deletedDisputesLoading, setDeletedDisputesLoading] = useState(false);

  // ========== DATA STATES ==========
  const [currentUser, setCurrentUser] = useState(null);
  const [leadOptions, setLeadOptions] = useState([]);
  // Pending debounce timer for the lead lookup, and a counter identifying the
  // most recent request so older ones can be ignored — see fetchLeads.
  const leadSearchTimerRef = useRef(null);
  const leadSearchSeqRef = useRef(0);

  // Anchors the sticky table headers — see getStickyContainer.
  const pageRef = useRef(null);
  const [disputes, setDisputes] = useState([]);
  const [deletedDisputes, setDeletedDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [leadDetailsCache, setLeadDetailsCache] = useState({});
  const [loadingLeadDetails, setLoadingLeadDetails] = useState({});

  // Pre-flight duplicate-dispute check (create form) — { hasDuplicate, data } | null
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Status History modal
  const [statusHistoryModalVisible, setStatusHistoryModalVisible] = useState(false);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [statusHistoryData, setStatusHistoryData] = useState([]);
  const [statusHistoryRecord, setStatusHistoryRecord] = useState(null);

  // Field History modal — one modal shared by the Dispute Created Date, Dispute
  // Reason and Comments History buttons; `fieldHistoryField` says which log is open.
  const [fieldHistoryField, setFieldHistoryField] = useState(null);
  const [fieldHistoryLoading, setFieldHistoryLoading] = useState(false);
  const [fieldHistoryData, setFieldHistoryData] = useState([]);
  const [fieldHistoryRecord, setFieldHistoryRecord] = useState(null);

  // Inline status editing — the pencil next to a row's status tag. Only one row
  // can be open at a time, so the row's id doubles as the "is the editor open" flag.
  const [statusEditRowId, setStatusEditRowId] = useState(null);
  const [statusEditValue, setStatusEditValue] = useState(null);
  const [statusEditDate, setStatusEditDate] = useState(null);
  const [statusEditResolutionType, setStatusEditResolutionType] = useState(null);
  const [statusEditSaving, setStatusEditSaving] = useState(false);

  // ========== PAGINATION STATES ==========
  const [activePagination, setActivePagination] = useState({
    current: 1,
    pageSize: 24,
    total: 0,
  });
  const [deletedPagination, setDeletedPagination] = useState({
    current: 1,
    pageSize: 24,
    total: 0,
  });

  // ========== SELECTION STATES ==========
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deletedSelectedRowKeys, setDeletedSelectedRowKeys] = useState([]);

  // ========== FILTER STATES ==========
  const [searchText, setSearchText] = useState('');
  const [immediateSearchText, setImmediateSearchText] = useState('');
  const [localSearchValue, setLocalSearchValue] = useState('');

  const [quickFilter, setQuickFilter] = useState('thisMonth');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Bumped to force a re-fetch of the current tab (Refresh button, after a
  // mutation) even when pagination/filter values happen to already match —
  // React skips an effect when none of its dependencies actually changed value,
  // so a plain "reset to page 1" isn't enough if we were already on page 1.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ========== FORM INSTANCES ==========
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // ========== HELPER FUNCTIONS ==========
  // antd sticks a sticky header to the window by default, but this app doesn't
  // scroll the window: the page renders inside <Content>, which is a fixed
  // 100vh-minus-header box with its own overflow (see App.jsx). Sticking to the
  // window would leave the header pinned to a viewport edge the table never
  // reaches, so the real scrolling ancestor is resolved here and handed to antd.
  //
  // Walked from the page element rather than looked up by class, so it keeps
  // working if the page is ever mounted somewhere else; falls back to the window,
  // which is antd's own default, when nothing above it scrolls.
  const getStickyContainer = () => {
    let node = pageRef.current?.parentElement;

    while (node && node !== document.body) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return node;
      }
      node = node.parentElement;
    }

    return window;
  };

  const sortByLatestFirst = (data) => {
    return [...data].sort((a, b) => {
      const dateA = a.created_at || a.createdAt;
      const dateB = b.created_at || b.createdAt;
      return new Date(dateB) - new Date(dateA);
    });
  };

  // Ranges are computed off "now in Pacific" and sent as bare YYYY-MM-DD, which
  // the backend reads as Pacific days — so "Today" means the Pacific day for every
  // agent, not whichever day it happens to be in their own timezone.
  const getDateRangeFromFilter = (filter) => {
    const now = nowInPST();
    const startOfToday = nowInPST().startOf('day');
    const endOfToday = nowInPST().endOf('day');
    
    switch(filter) {
      case 'today':
        return [startOfToday, endOfToday];
      case 'yesterday':
        return [now.subtract(1, 'days').startOf('day'), now.subtract(1, 'days').endOf('day')];
      case 'thisWeek':
        return [now.startOf('week'), now.endOf('week')];
      case 'last7days':
        return [now.subtract(7, 'days').startOf('day'), endOfToday];
      case 'last30days':
        return [now.subtract(30, 'days').startOf('day'), endOfToday];
      case 'thisMonth':
        return [now.startOf('month'), now.endOf('month')];
      case 'lastMonth':
        return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
      case 'thisQuarter':
        return [now.startOf('quarter'), now.endOf('quarter')];
      case 'thisYear':
        return [now.startOf('year'), now.endOf('year')];
      case 'lastYear':
        return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
      case 'custom':
        return null;
      default:
        return [now.startOf('month'), now.endOf('month')];
    }
  };

  // ========== LIST REQUEST PARAMS ==========
  // Search/date-range/pagination are now sent to the backend on every list
  // request instead of filtering an already-fetched array client-side — see
  // fetchActiveDisputes/fetchDeletedDisputes. `pagination` is whichever of
  // activePagination/deletedPagination belongs to the list being fetched.
  const buildListParams = (pagination) => {
    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
    };

    if (searchText.trim() && searchText.trim().length >= MIN_SEARCH_CHARS) {
      params.search = searchText.trim();
    }

    if (statusFilter && statusFilter !== 'ALL') {
      params.status = statusFilter;
    }

    let dateRange = null;
    if (quickFilter === 'custom' && filterDateRange && filterDateRange.length === 2) {
      dateRange = filterDateRange;
    } else if (quickFilter !== 'custom') {
      dateRange = getDateRangeFromFilter(quickFilter);
    }
    if (dateRange && dateRange.length === 2) {
      params.from_date = dateRange[0].format('YYYY-MM-DD');
      params.to_date = dateRange[1].format('YYYY-MM-DD');
    }

    return params;
  };

  const resetFilters = () => {
    setImmediateSearchText('');
    setSearchText('');
    setQuickFilter('thisMonth');
    setShowCustomRange(false);
    setFilterDateRange(null);
    setStatusFilter('ALL');
    setActivePagination(prev => ({ ...prev, current: 1 }));
    setDeletedPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setActivePagination(prev => ({ ...prev, current: 1 }));
    setDeletedPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleQuickFilterChange = (value) => {
    setQuickFilter(value);
    setActivePagination(prev => ({ ...prev, current: 1 }));
    setDeletedPagination(prev => ({ ...prev, current: 1 }));
    
    if (value === 'custom') {
      setShowCustomRange(true);
      setFilterDateRange(null);
    } else {
      setShowCustomRange(false);
      const newDateRange = getDateRangeFromFilter(value);
      setFilterDateRange(newDateRange);
    }
  };

  const handleCustomRangeChange = (dates) => {
    setActivePagination(prev => ({ ...prev, current: 1 }));
    setDeletedPagination(prev => ({ ...prev, current: 1 }));
    if (dates && dates[0] && dates[1]) {
      setFilterDateRange(dates);
      setQuickFilter('custom');
    } else {
      setQuickFilter('thisMonth');
      setShowCustomRange(false);
      setFilterDateRange(null);
    }
  };

  const executeSearch = (value) => {
    if (value.trim().length >= MIN_SEARCH_CHARS || value.trim() === '') {
      setSearchText(value);
      setImmediateSearchText(value);
      if (activeTab === 'active') {
        setActivePagination(prev => ({ ...prev, current: 1 }));
      } else if (activeTab === 'deleted') {
        setDeletedPagination(prev => ({ ...prev, current: 1 }));
      }
    } else if (value.trim().length > 0 && value.trim().length < MIN_SEARCH_CHARS) {
      message.warning(`Please enter at least ${MIN_SEARCH_CHARS} characters to search`);
    }
  };

  const handleClearSearchFilter = () => {
    setSearchText('');
    setImmediateSearchText('');
    setLocalSearchValue('');
    if (activeTab === 'active') {
      setActivePagination(prev => ({ ...prev, current: 1 }));
    } else if (activeTab === 'deleted') {
      setDeletedPagination(prev => ({ ...prev, current: 1 }));
    }
  };

  const handleClearCustomDateFilter = () => {
    setQuickFilter('thisMonth');
    setShowCustomRange(false);
    setFilterDateRange(null);
    if (activeTab === 'active') {
      setActivePagination(prev => ({ ...prev, current: 1 }));
    } else if (activeTab === 'deleted') {
      setDeletedPagination(prev => ({ ...prev, current: 1 }));
    }
  };

  const handleActivePaginationChange = (page, pageSize) => {
    setActivePagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const handleDeletedPaginationChange = (page, pageSize) => {
    setDeletedPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  // ========== API CALLS ==========
  const fetchCurrentUser = async () => {
    try {
      const response = await API.getCurrentUser();
      if (response?.data) {
        setCurrentUser(response.data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchLeadDetails = async (leadId) => {
    if (!leadId) return null;
    
    if (leadDetailsCache[leadId]) {
      return leadDetailsCache[leadId];
    }

    if (loadingLeadDetails[leadId]) {
      return null;
    }

    setLoadingLeadDetails(prev => ({ ...prev, [leadId]: true }));
    
    try {
      const response = await API.GetLeadById(leadId);
      
      let leadData = null;
      if (response?.status && response?.data) {
        leadData = response.data;
      } else if (response?.data) {
        leadData = response.data;
      } else if (response?.lead) {
        leadData = response.lead;
      } else {
        leadData = response;
      }
      
      if (leadData) {
        const leadInfo = {
          fullName: leadData.personalInfo?.fullName || 'N/A',
          email: leadData.personalInfo?.email || 'N/A',
          phoneNumber: leadData.personalInfo?.phoneNumber || 'N/A',
          zipCode: leadData.personalInfo?.zipCode || 'N/A',
          customerTimeZone: leadData.personalInfo?.customerTimeZone || 'N/A',
          orderNumber: leadData.orderNumber || 'N/A',
          leadStage: leadData.leadStage || 'N/A',
          status: leadData.status || 'N/A',
          vehicleInfo: {
            make: leadData.vehicleInfo?.make || 'N/A',
            model: leadData.vehicleInfo?.model || 'N/A',
            year: leadData.vehicleInfo?.year || 'N/A',
            part: leadData.vehicleInfo?.part || 'N/A',
            vin: leadData.vehicleInfo?.vin || 'N/A'
          },
          saleMadeCharge: {
            customerAmountReceived: leadData.saleMadeCharge?.customerAmountReceived || 0,
            yardPrice: leadData.saleMadeCharge?.yardPrice || 0,
            customerCCFee: leadData.saleMadeCharge?.customerCCFee || 0,
            totalGP: leadData.saleMadeCharge?.totalGP || 0
          },
          yard: leadData.yard || [],
          advancePayments: leadData.advancePayments || [],
          totalAmount: leadData.totalAmount || 0,
          paidAmount: leadData.paidAmount || 0,
          balanceAmount: leadData.balanceDue || (leadData.totalAmount - leadData.paidAmount) || 0,
          currentAgentName: leadData.currentAgentName || 
                           (leadData.handledBy?.firstName && leadData.handledBy?.lastName 
                             ? `${leadData.handledBy.firstName} ${leadData.handledBy.lastName}`
                             : leadData.handledBy?.name || 'N/A'),
          handledBy: leadData.handledBy || 'N/A',
          shippingAddress: leadData.shippingAddress || {},
          billingAddress: leadData.billingAddress || {}
        };
        
        setLeadDetailsCache(prev => ({ ...prev, [leadId]: leadInfo }));
        setLoadingLeadDetails(prev => ({ ...prev, [leadId]: false }));
        return leadInfo;
      }
    } catch (error) {
      console.error(`Error fetching lead details for ${leadId}:`, error);
      setLoadingLeadDetails(prev => ({ ...prev, [leadId]: false }));
      return null;
    }
    return null;
  };

  const fetchActiveDisputes = async () => {
    setDisputesLoading(true);
    try {
      const response = await API.getActiveDisputes(buildListParams(activePagination));

      let disputesData = [];
      let paginationMeta = null;
      if (response?.success && response?.data) {
        disputesData = response.data;
        paginationMeta = response.pagination;
      } else if (response?.data) {
        disputesData = response.data;
        paginationMeta = response.pagination;
      } else if (Array.isArray(response)) {
        disputesData = response;
      }

      // No client-side sort or per-row lead fetch: the backend now returns this
      // page already sorted (created_at + _id tiebreak) and with lead_id fully
      // populated (personalInfo, orderNumber, vehicleInfo, saleMadeCharge, yard,
      // advancePayments, etc.) — see getAllActiveDisputes / LEAD_LIST_FIELDS.
      setDisputes(disputesData);
      setActivePagination(prev => ({ ...prev, total: paginationMeta?.total ?? disputesData.length }));
    } catch (error) {
      console.error('Error fetching active disputes:', error);
      message.error('Failed to fetch active disputes');
      setDisputes([]);
    } finally {
      setDisputesLoading(false);
    }
  };

  const fetchDeletedDisputes = async () => {
    setDeletedDisputesLoading(true);
    try {
      // Pagination/search/date-range now go to the backend like the active list
      // (getAllDeletedDisputes already supported these params). Its populate
      // wasn't widened in this phase (out of scope — only getAllActiveDisputes
      // was), so lead details still come from the per-row fetchLeadDetails loop
      // below, same as before. One consequence: server-side `search` here only
      // matches description/resolution_notes (the endpoint's existing behavior)
      // — it does not reach lead name/email/phone/order the way the active list's
      // search now does, since that required the populate widening this endpoint
      // didn't get. Flagged as a candidate for a follow-up scoped fix.
      const response = await API.getDeletedDisputes(buildListParams(deletedPagination));

      let disputesData = [];
      let paginationMeta = null;
      if (response?.success && response?.data) {
        disputesData = response.data;
        paginationMeta = response.pagination;
      } else if (response?.data) {
        disputesData = response.data;
        paginationMeta = response.pagination;
      } else if (Array.isArray(response)) {
        disputesData = response;
      }

      const sortedDisputes = sortByLatestFirst(disputesData);
      setDeletedDisputes(sortedDisputes);
      setDeletedPagination(prev => ({ ...prev, total: paginationMeta?.total ?? sortedDisputes.length }));

      // The per-row lead fetch that used to run here is gone: this endpoint now
      // populates the lead fields the columns render (see getAllDeletedDisputes),
      // so the cells derive what they need from the row via leadDetailsFromRow.
      // That loop awaited one request per row, so a full page cost up to 24
      // sequential round trips before the tab settled.

      if (sortedDisputes.length === 0) {
        message.info('No deleted disputes found');
      }
    } catch (error) {
      console.error('Error fetching deleted disputes:', error);
      message.error('Failed to fetch deleted disputes');
      setDeletedDisputes([]);
    } finally {
      setDeletedDisputesLoading(false);
    }
  };

  // The backend matches the term against the lead's full name, email, phone and
  // order number (among other fields), case-insensitively — so nothing needs
  // filtering here, which is why the Select sets filterOption={false}.
  //
  // Every call takes a sequence number and only the newest is allowed to write
  // results. Searches are issued per keystroke, and a broad early term ("T")
  // matches far more leads than the finished one ("Test"), so it takes longer to
  // come back and used to land last — replacing the correct results with a list
  // that had nothing to do with what the user had typed.
  const fetchLeads = async (search = '') => {
    const requestId = leadSearchSeqRef.current + 1;
    leadSearchSeqRef.current = requestId;
    const isCurrent = () => requestId === leadSearchSeqRef.current;

    setLeadLoading(true);
    try {
      const response = await API.leadGetAll({
        page: 1,
        limit: LEAD_SEARCH_LIMIT,
        search: search,
        // Lead picker: name, email, phone, ZIP, order # and VIN are enough to
        // find a lead, and much faster than searching every lead field.
        searchFields: 'quick',
        status: "Sales Made",
        fields: LEAD_SEARCH_FIELDS,
      });

      if (!isCurrent()) return;

      if (response?.status && response?.data) {
        const options = response.data.map(lead => ({
          value: lead._id,
          label: `${lead.personalInfo?.fullName || 'N/A'} | ${lead.orderNumber || 'N/A'} (${lead.personalInfo?.email || 'No email'})`,
          owner_id: lead.handledBy?._id || lead.owner_id
        }));
        setLeadOptions(options);
      }
    } catch (error) {
      if (isCurrent()) {
        console.error('Error fetching leads:', error);
      }
    } finally {
      // A superseded request must not clear the spinner — the newer one that
      // overtook it is still in flight.
      if (isCurrent()) {
        setLeadLoading(false);
      }
    }
  };

  // Debounced entry point for the drawer's lead dropdown. Typing a five-letter
  // name used to issue five requests; now it issues one.
  const handleLeadSearch = (value) => {
    if (leadSearchTimerRef.current) {
      clearTimeout(leadSearchTimerRef.current);
    }

    // Show the spinner from the first keystroke rather than when the request
    // finally goes out, so the pause doesn't read as a dropdown that has died.
    setLeadLoading(true);

    leadSearchTimerRef.current = setTimeout(() => {
      leadSearchTimerRef.current = null;
      fetchLeads((value || '').trim());
    }, LEAD_SEARCH_DEBOUNCE_MS);
  };

  // Drops a queued search that no longer has anywhere to land — on drawer close,
  // and on unmount, where firing it would set state on a gone component.
  const cancelPendingLeadSearch = () => {
    if (leadSearchTimerRef.current) {
      clearTimeout(leadSearchTimerRef.current);
      leadSearchTimerRef.current = null;
    }
    // Retires every in-flight request too, so none of them writes results into
    // the next drawer session.
    leadSearchSeqRef.current += 1;
  };

  // ========== CRUD OPERATIONS ==========
  const handleSoftDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select disputes to delete');
      return;
    }

    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to soft delete ${selectedRowKeys.length} dispute(s)? These disputes can be restored later.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeleteLoading(true);
        try {
          const response = await API.softDeleteDisputes(selectedRowKeys);
          
          if (response?.success) {
            message.success(response.message || `${selectedRowKeys.length} dispute(s) deleted successfully`);
            setSelectedRowKeys([]);

            if (activeTab === 'active') {
              setActivePagination(prev => ({ ...prev, current: 1 }));
              setRefreshNonce(n => n + 1);
            }
          } else {
            message.error(response?.message || 'Failed to delete disputes');
          }
        } catch (error) {
          console.error('Error deleting disputes:', error);
          message.error('Failed to delete disputes');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const handlePermanentDelete = async () => {
    if (deletedSelectedRowKeys.length === 0) {
      message.warning('Please select disputes to permanently delete');
      return;
    }

    Modal.confirm({
      title: 'Confirm Permanent Delete',
      content: (
        <div>
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            ⚠️ WARNING: This action is irreversible!
          </p>
          <p>
            Are you sure you want to permanently delete {deletedSelectedRowKeys.length} dispute(s)?
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: 'Yes, Permanently Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setPermanentDeleteLoading(true);
        try {
          const response = await API.hardDeleteDisputes(deletedSelectedRowKeys);
          
          if (response?.success) {
            message.success(response.message || `${deletedSelectedRowKeys.length} dispute(s) permanently deleted`);
            setDeletedSelectedRowKeys([]);
            setDeletedPagination(prev => ({ ...prev, current: 1 }));
            setRefreshNonce(n => n + 1);

            if (activeTab === 'active') {
              await fetchActiveDisputes();
            }
          } else {
            message.error(response?.message || 'Failed to permanently delete disputes');
          }
        } catch (error) {
          console.error('Error permanently deleting disputes:', error);
          message.error('Failed to permanently delete disputes');
        } finally {
          setPermanentDeleteLoading(false);
        }
      }
    });
  };

  const handlePermanentDeleteSingle = async (dispute) => {
    Modal.confirm({
      title: 'Confirm Permanent Delete',
      content: (
        <div>
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            ⚠️ WARNING: This action is irreversible!
          </p>
          <p>
            Are you sure you want to permanently delete this dispute?
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: 'Yes, Permanently Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setPermanentDeleteLoading(true);
        try {
          const response = await API.hardDeleteDisputes([dispute._id]);
          
          if (response?.success) {
            message.success('Dispute permanently deleted');
            setDeletedPagination(prev => ({ ...prev, current: 1 }));
            setRefreshNonce(n => n + 1);

            if (activeTab === 'active') {
              await fetchActiveDisputes();
            }
          } else {
            message.error(response?.message || 'Failed to permanently delete dispute');
          }
        } catch (error) {
          console.error('Error permanently deleting dispute:', error);
          message.error('Failed to permanently delete dispute');
        } finally {
          setPermanentDeleteLoading(false);
        }
      }
    });
  };

  const handleRestoreDisputes = async () => {
    if (deletedSelectedRowKeys.length === 0) {
      message.warning('Please select disputes to restore');
      return;
    }

    Modal.confirm({
      title: 'Confirm Restore',
      content: `Are you sure you want to restore ${deletedSelectedRowKeys.length} dispute(s)?`,
      okText: 'Yes, Restore',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setRestoreLoading(true);
        try {
          const response = await API.restoreDisputes(deletedSelectedRowKeys);
          
          if (response?.success) {
            message.success(response.message || `${deletedSelectedRowKeys.length} dispute(s) restored successfully`);
            setDeletedSelectedRowKeys([]);
            setDeletedPagination(prev => ({ ...prev, current: 1 }));
            setActivePagination(prev => ({ ...prev, current: 1 }));
            setRefreshNonce(n => n + 1);

            if (activeTab === 'active') {
              await fetchActiveDisputes();
            }
          } else {
            message.error(response?.message || 'Failed to restore disputes');
          }
        } catch (error) {
          console.error('Error restoring disputes:', error);
          message.error('Failed to restore disputes');
        } finally {
          setRestoreLoading(false);
        }
      }
    });
  };

  const handleRestoreSingleDispute = async (dispute) => {
    Modal.confirm({
      title: 'Confirm Restore',
      content: `Are you sure you want to restore this dispute?`,
      okText: 'Yes, Restore',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setRestoreLoading(true);
        try {
          const response = await API.restoreDisputes([dispute._id]);
          
          if (response?.success) {
            message.success('Dispute restored successfully');
            setDeletedPagination(prev => ({ ...prev, current: 1 }));
            setRefreshNonce(n => n + 1);

            if (activeTab === 'active') {
              await fetchActiveDisputes();
            }
          } else {
            message.error(response?.message || 'Failed to restore dispute');
          }
        } catch (error) {
          console.error('Error restoring dispute:', error);
          message.error('Failed to restore dispute');
        } finally {
          setRestoreLoading(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    // Client-side pre-flight only — the server (createDispute's own guard) is
    // still the authoritative check, in case this result is stale (e.g. someone
    // else opened a dispute on this lead between the check and this submit).
    if (duplicateCheck?.hasDuplicate) {
      message.error('This lead already has an active dispute — resolve or close it first.');
      return;
    }

    setLoading(true);
    try {
      // status/priority default via each field's initialValue when left untouched.
      // sla_deadline defaults server-side from priority when omitted — only
      // convert it here if the user actually picked a date.
      const payload = {
        ...values,
        // The pickers hold Pacific wall-clock times in local dayjs objects (see
        // toPickerValue/fromPickerValue) — re-anchor them to Pacific here so the
        // instant that reaches the server is the one the user actually saw.
        dispute_date: fromPickerValue(values.dispute_date) || undefined,
        sla_deadline: fromPickerValue(values.sla_deadline) || undefined
      };

      const response = await API.createDispute(payload);

      if (response?.success) {
        message.success('Dispute created successfully');
        handleCloseDrawer();
        fetchActiveDisputes();
      } else {
        message.error(response?.message || 'Failed to create dispute');
      }
    } catch (error) {
      console.error('Error creating dispute:', error);

      if (error.response) {
        const errorMessage = error.response.data?.message ||
                            error.response.data?.error ||
                            'Server error occurred';
        message.error(errorMessage);
      } else if (error.request) {
        message.error('Network error: Could not connect to server');
      } else {
        message.error(error.message || 'Failed to create dispute');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async (values) => {
    if (!selectedDispute?._id) {
      message.error('No dispute selected for update');
      return;
    }
    
    setEditLoading(true);
    try {
      const payload = {
        dispute_reason: values.dispute_reason,
        description: values.description,
        dispute_date: fromPickerValue(values.dispute_date) || undefined,
        status: values.status,
        priority: values.priority,
        sla_deadline: fromPickerValue(values.sla_deadline) || undefined,
        resolution_type: values.resolution_type,
        resolution_notes: values.resolution_notes,
        resolved_at: fromPickerValue(values.resolved_at) || undefined
      };
      
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });
      
      const response = await API.updateDispute(selectedDispute._id, payload);
      
      if (response?.success || response?.status === true) {
        message.success('Dispute updated successfully');
        handleCloseEditDrawer();
        await refreshCurrentTabData();
      } else {
        message.error(response?.message || 'Failed to update dispute');
      }
    } catch (error) {
      console.error('Error updating dispute:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 
                            error.response.data?.error || 
                            'Server error occurred';
        message.error(errorMessage);
      } else if (error.request) {
        message.error('Network error: Could not connect to server');
      } else {
        message.error(error.message || 'Failed to update dispute');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const refreshCurrentTabData = async () => {
    if (activeTab === 'active') {
      await fetchActiveDisputes();
    } else if (activeTab === 'deleted') {
      await fetchDeletedDisputes();
    }
  };

  // ========== UI HANDLERS ==========
  const handleOpenDrawer = () => {
    setDrawerVisible(true);
    setDuplicateCheck(null);
    form.resetFields();
    cancelPendingLeadSearch();
    fetchLeads();

    // Set here rather than as the field's initialValue: resetFields() would put
    // back the value captured when the drawer first mounted, so reopening it
    // hours later would prefill a stale "now".
    form.setFieldsValue({
      dispute_date: nowInPST(),
      ...(currentUser
        ? { disputed_by_user_id: currentUser._id, status: 'OPEN', priority: 'MEDIUM' }
        : {})
    });
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setDuplicateCheck(null);
    form.resetFields();
    cancelPendingLeadSearch();
    setLeadLoading(false);
  };

  const handleOpenEditDrawer = (dispute) => {
    setSelectedDispute(dispute);
    setEditDrawerVisible(true);
    
    const formValues = {
      dispute_reason: dispute.dispute_reason,
      description: dispute.description,
      // Falls back to created_at for disputes written before dispute_date existed,
      // so the picker opens on the date the row has always displayed.
      dispute_date: toPickerValue(getDisputeDate(dispute)),
      status: dispute.status,
      priority: dispute.priority,
      sla_deadline: toPickerValue(dispute.sla_deadline),
      resolution_type: dispute.resolution_type,
      resolution_notes: dispute.resolution_notes,
      resolved_at: toPickerValue(dispute.resolved_at)
    };
    
    editForm.setFieldsValue(formValues);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerVisible(false);
    editForm.resetFields();
    setSelectedDispute(null);
  };

  const handleViewDetails = async (dispute) => {
    const leadId = dispute.lead_id?._id || dispute.lead_id;
    let leadDetails = leadDetailsCache[leadId];
    
    if (leadId && !leadDetails) {
      leadDetails = await fetchLeadDetails(leadId);
    }
    
    setSelectedDispute({
      ...dispute,
      leadDetails: leadDetails,
      extractedLeadId: leadId
    });
    setDetailModalVisible(true);
  };

  const handleViewStatusHistory = async (record) => {
    setStatusHistoryRecord(record);
    setStatusHistoryModalVisible(true);
    setStatusHistoryLoading(true);
    try {
      const response = await API.getDisputeStatusHistory(record._id);
      if (response?.success) {
        setStatusHistoryData(response.data || []);
      } else {
        message.error(response?.message || 'Failed to load status history');
        setStatusHistoryData([]);
      }
    } catch (error) {
      console.error('Error fetching status history:', error);
      message.error('Failed to load status history');
      setStatusHistoryData([]);
    } finally {
      setStatusHistoryLoading(false);
    }
  };

  const handleCloseStatusHistoryModal = () => {
    setStatusHistoryModalVisible(false);
    setStatusHistoryData([]);
    setStatusHistoryRecord(null);
  };

  // One handler for all three value logs — `field` is the schema field name, which
  // is also the endpoint's :field segment (dispute_date | dispute_reason | description).
  const handleViewFieldHistory = async (record, field) => {
    setFieldHistoryRecord(record);
    setFieldHistoryField(field);
    setFieldHistoryLoading(true);
    setFieldHistoryData([]);
    try {
      const response = await API.getDisputeFieldHistory(record._id, field);
      if (response?.success) {
        setFieldHistoryData(response.data || []);
      } else {
        message.error(response?.message || 'Failed to load history');
      }
    } catch (error) {
      console.error(`Error fetching ${field} history:`, error);
      message.error('Failed to load history');
    } finally {
      setFieldHistoryLoading(false);
    }
  };

  const handleCloseFieldHistoryModal = () => {
    setFieldHistoryField(null);
    setFieldHistoryData([]);
    setFieldHistoryRecord(null);
  };

  // ========== INLINE STATUS EDITING ==========
  const handleOpenStatusEditor = (record) => {
    setStatusEditRowId(record._id);
    setStatusEditValue(record.status);
    // Defaults to now in Pacific; the picker lets the user backdate the change.
    setStatusEditDate(nowInPST());
    setStatusEditResolutionType(record.resolution_type || null);
  };

  const handleCloseStatusEditor = () => {
    setStatusEditRowId(null);
    setStatusEditValue(null);
    setStatusEditDate(null);
    setStatusEditResolutionType(null);
  };

  const handleSaveInlineStatus = async (record) => {
    if (!statusEditValue) {
      message.warning('Please select a status');
      return;
    }

    // Same rule the backend enforces on any transition to RESOLVED — checked here
    // so the user gets it in the popover they're already looking at, rather than
    // as a 400 after the request round-trips.
    if (statusEditValue === 'RESOLVED' && !statusEditResolutionType) {
      message.warning('Please select a resolution type to resolve this dispute');
      return;
    }

    if (statusEditValue === record.status) {
      handleCloseStatusEditor();
      return;
    }

    setStatusEditSaving(true);
    try {
      const payload = {
        status: statusEditValue,
        // Not a schema field — the date the change itself is recorded under in
        // status_history (see updateDispute).
        status_changed_at: fromPickerValue(statusEditDate || nowInPST()),
      };
      if (statusEditValue === 'RESOLVED') {
        payload.resolution_type = statusEditResolutionType;
      }

      const response = await API.updateDispute(record._id, payload);

      if (response?.success || response?.status === true) {
        message.success('Status updated successfully');
        handleCloseStatusEditor();
        await refreshCurrentTabData();
      } else {
        message.error(response?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status inline:', error);
      message.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusEditSaving(false);
    }
  };

  const handleRefresh = () => {
    // Active/Deleted refetch reactively (see the fetch effects below) once
    // pagination resets to page 1 and refreshNonce bumps — bumping the nonce
    // guarantees a re-fetch even when we're already on page 1, where the
    // pagination reset alone wouldn't change anything for the effect to react to.
    setActivePagination(prev => ({ ...prev, current: 1 }));
    setDeletedPagination(prev => ({ ...prev, current: 1 }));
    setRefreshNonce(n => n + 1);
  };

  const handleLeadSelect = async (leadId) => {
    setDuplicateCheck(null);
    if (!leadId) return;

    setCheckingDuplicate(true);
    try {
      const response = await API.checkDuplicateDispute(leadId);
      if (response?.success) {
        setDuplicateCheck(response);
      }
    } catch (error) {
      console.error('Error checking for duplicate dispute:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Wired to <Tabs onChange> instead of setActiveTab directly: batching the tab
  // change with the selection/filter reset into one handler means React commits
  // them in a single render, so the fetch effects above see the final reset state
  // immediately — calling setActiveTab alone and resetting filters in a separate
  // effect would fire the fetch twice (once with stale filters, once corrected).
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedRowKeys([]);
    setDeletedSelectedRowKeys([]);
    resetFilters();
  };

  // ========== RENDER FUNCTIONS ==========
  const getStatusTag = (status) => {
    const statusConfig = {
      'OPEN': { color: 'processing', text: 'Open', icon: <ClockCircleOutlined /> },
      'UNDER_REVIEW': { color: 'warning', text: 'Under Review', icon: <ClockCircleOutlined /> },
      'RESOLVED': { color: 'success', text: 'Resolved', icon: <CheckCircleOutlined /> },
      'LOSS': { color: 'error', text: 'Loss', icon: <CloseCircleOutlined /> }
    };
    const config = statusConfig[status] || { color: 'default', text: status, icon: null };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getPriorityTag = (priority) => {
    const priorityConfig = {
      'LOW': { color: 'blue', text: 'Low' },
      'MEDIUM': { color: 'orange', text: 'Medium' },
      'HIGH': { color: 'red', text: 'High' },
      'CRITICAL': { color: 'purple', text: 'Critical' }
    };
    const config = priorityConfig[priority] || { color: 'default', text: priority };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // ========== SHARED VALUE COLUMNS ==========
  // Dispute Reason, Status, Dispute Created Date, SLA Deadline and Comments are
  // identical on both tabs, and each now carries a History button (and, for
  // Status, an inline editor) — so they're built once here and spread into both
  // column arrays below instead of being maintained as two copies.
  //
  // `allowInlineStatusEdit` is false on the Deleted tab: updateDispute only
  // matches non-deleted disputes, so a pencil there could only ever 404. Restore
  // first, then edit.
  //
  // `defaultSortByDate` is likewise Active-only. A table's default sort re-orders
  // the page the server sent, so it belongs on the tab whose server ordering it
  // agrees with — the Active list is ordered newest-dispute-first, while the
  // Deleted list is ordered by when each dispute was binned.
  const buildValueColumns = ({ allowInlineStatusEdit, defaultSortByDate }) => [
    {
      title: 'Dispute Reason',
      dataIndex: 'dispute_reason',
      key: 'dispute_reason',
      width: COLUMN_WIDTH.disputeReason,
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          <span style={wrappedCellStyle}>{text?.replace(/_/g, ' ') || 'N/A'}</span>
          <HistoryLink onClick={() => handleViewFieldHistory(record, 'dispute_reason')} />
        </Space>
      ),
      sorter: (a, b) => (a.dispute_reason || '').localeCompare(b.dispute_reason || '')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: COLUMN_WIDTH.status,
      render: (status, record) => (
        <Space direction="vertical" size={2}>
          <Space size={4}>
            {getStatusTag(status)}
            {allowInlineStatusEdit && (
              <Popover
                open={statusEditRowId === record._id}
                trigger="click"
                placement="bottomLeft"
                onOpenChange={(open) => {
                  if (open) {
                    handleOpenStatusEditor(record);
                  } else if (!statusEditSaving) {
                    handleCloseStatusEditor();
                  }
                }}
                title="Change Status"
                content={
                  <div style={{ width: 260 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Select
                        value={statusEditValue}
                        onChange={setStatusEditValue}
                        style={{ width: '100%' }}
                        placeholder="Select status"
                      >
                        <Option value="OPEN">Open</Option>
                        <Option value="UNDER_REVIEW">Under Review</Option>
                        <Option value="RESOLVED">Resolved</Option>
                        <Option value="LOSS">Loss</Option>
                      </Select>
                    </div>

                    {statusEditValue === 'RESOLVED' && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Resolution Type</div>
                        <Select
                          value={statusEditResolutionType}
                          onChange={setStatusEditResolutionType}
                          style={{ width: '100%' }}
                          placeholder="Select resolution type"
                        >
                          <Option value="WIN">Win</Option>
                          <Option value="LOSS">Loss</Option>
                        </Select>
                      </div>
                    )}

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Changed On (PST)</div>
                      <DatePicker
                        showTime={{ format: 'HH:mm' }}
                        format={PST_FORMAT}
                        value={statusEditDate}
                        onChange={setStatusEditDate}
                        allowClear={false}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <Space style={{ float: 'right' }}>
                      <Button size="small" onClick={handleCloseStatusEditor} disabled={statusEditSaving}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={statusEditSaving}
                        onClick={() => handleSaveInlineStatus(record)}
                      >
                        Save
                      </Button>
                    </Space>
                    <div style={{ clear: 'both' }} />
                  </div>
                }
              >
                <Tooltip title="Edit status">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    style={{ padding: 0, height: 'auto', color: '#1890ff' }}
                  />
                </Tooltip>
              </Popover>
            )}
          </Space>
          <HistoryLink onClick={() => handleViewStatusHistory(record)} />
        </Space>
      ),
      sorter: (a, b) => (a.status || '').localeCompare(b.status || '')
    },
    {
      title: 'Dispute Created Date',
      dataIndex: 'dispute_date',
      key: 'dispute_date',
      width: COLUMN_WIDTH.disputeDate,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <span style={{ whiteSpace: 'nowrap' }}>{formatPST(getDisputeDate(record))}</span>
          <HistoryLink onClick={() => handleViewFieldHistory(record, 'dispute_date')} />
        </Space>
      ),
      // Ascending comparator on purpose. antd applies it as-is for 'ascend' and
      // negates it for 'descend' (useSorter.js), so writing it newest-first here
      // and asking for 'descend' cancels out and puts the OLDEST dispute on top —
      // which is exactly what was pushing newly created disputes down the list.
      sorter: (a, b) => new Date(getDisputeDate(a) || 0) - new Date(getDisputeDate(b) || 0),
      ...(defaultSortByDate ? { defaultSortOrder: 'descend' } : {})
    },
    {
      title: 'SLA Deadline',
      dataIndex: 'sla_deadline',
      key: 'sla_deadline',
      width: COLUMN_WIDTH.slaDeadline,
      render: (date) => {
        if (!date) return '48 hours';
        const isOverdue = dayjs(date).isBefore(dayjs());
        return (
          <span style={{ color: isOverdue ? 'red' : 'inherit', whiteSpace: 'nowrap' }}>
            {formatPST(date)}
            {isOverdue && <Badge status="error" style={{ marginLeft: 8 }} />}
          </span>
        );
      },
      sorter: (a, b) => {
        const dateA = new Date(a.sla_deadline || 0);
        const dateB = new Date(b.sla_deadline || 0);
        return dateA - dateB;
      }
    },
    {
      title: 'Comments',
      dataIndex: 'description',
      key: 'description',
      width: COLUMN_WIDTH.comments,
      render: (text, record) => {
        const cleaned = text ? text.replace(/_/g, ' ') : '';
        const maxLength = 50;

        return (
          <Space direction="vertical" size={2}>
            {!cleaned ? (
              <span>N/A</span>
            ) : cleaned.length <= maxLength ? (
              <span style={wrappedCellStyle}>{cleaned}</span>
            ) : (
              <Tooltip title={cleaned}>
                <span style={wrappedCellStyle}>{cleaned.substring(0, maxLength)}...</span>
              </Tooltip>
            )}
            <HistoryLink onClick={() => handleViewFieldHistory(record, 'description')} />
          </Space>
        );
      },
    },
  ];

  // ========== TABLE COLUMNS ==========
  // useMemo so the array (and the objects/closures inside it) aren't rebuilt on
  // every render — e.g. while typing in an unrelated field. Only recomputes when
  // one of the handlers it actually calls changes. The inline status editor's
  // state is listed too: the Status cell renders its open popover, so the columns
  // have to rebuild while a status is being edited.
  const columns = useMemo(() => [
    {
      title: 'Lead Info',
      dataIndex: 'lead_id',
      key: 'lead_info',
      width: COLUMN_WIDTH.leadInfo,
      render: (leadIdObj, record) => <LeadInfoCellPopulated record={record} />,
      sorter: (a, b) => {
        const leadA = a.lead_id?.personalInfo?.fullName || '';
        const leadB = b.lead_id?.personalInfo?.fullName || '';
        return leadA.localeCompare(leadB);
      }
    },

    ...buildValueColumns({ allowInlineStatusEdit: true, defaultSortByDate: true }),
    {
      title: 'Actions',
      key: 'actions',
      width: COLUMN_WIDTH.actions,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditDrawer(record)}
          >
            Edit
          </Button>
        </Space>
      )
    }
  ], [getStatusTag, handleViewDetails, handleOpenEditDrawer, handleViewStatusHistory, handleViewFieldHistory, statusEditRowId, statusEditValue, statusEditDate, statusEditResolutionType, statusEditSaving]);

  // See the `columns` useMemo above for why. Depends on leadDetailsCache too,
  // since the Lead Info/FINANCIAL cells here still read it (this endpoint's
  // populate wasn't widened — see LeadInfoCell/FinancialCell definitions).
  const deletedColumns = useMemo(() => [
    {
      title: 'Lead Info',
      dataIndex: 'lead_id',
      key: 'lead_info',
      width: COLUMN_WIDTH.leadInfo,
      render: (leadIdObj, record) => {
        const leadId = record.lead_id?._id || record.lead_id;
        return (
          <LeadInfoCell
            leadId={leadId}
            leadDetails={leadDetailsFromRow(record.lead_id) || leadDetailsCache[leadId]}
            isLoading={loadingLeadDetails[leadId]}
          />
        );
      },
      sorter: (a, b) => {
        const leadA = (leadDetailsFromRow(a.lead_id) || leadDetailsCache[a.lead_id?._id || a.lead_id])?.fullName || '';
        const leadB = (leadDetailsFromRow(b.lead_id) || leadDetailsCache[b.lead_id?._id || b.lead_id])?.fullName || '';
        return leadA.localeCompare(leadB);
      }
    },
    {
      title: 'FINANCIAL',
      key: 'financial',
      width: COLUMN_WIDTH.financial,
      sorter: (a, b) => {
        const chargeA = (leadDetailsFromRow(a.lead_id) || leadDetailsCache[a.lead_id?._id || a.lead_id])?.saleMadeCharge || {};
        const chargeB = (leadDetailsFromRow(b.lead_id) || leadDetailsCache[b.lead_id?._id || b.lead_id])?.saleMadeCharge || {};
        return (chargeA.customerAmountReceived || 0) - (chargeB.customerAmountReceived || 0);
      },
      render: (_, record) => {
        const leadId = record.lead_id?._id || record.lead_id;
        return <FinancialCell leadDetails={leadDetailsFromRow(record.lead_id) || leadDetailsCache[leadId]} />;
      },
    },

    ...buildValueColumns({ allowInlineStatusEdit: false, defaultSortByDate: false }),
    {
      title: 'Actions',
      key: 'actions',
      width: COLUMN_WIDTH.actionsDeleted,
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditDrawer(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Restore Dispute"
            description="Are you sure you want to restore this dispute?"
            onConfirm={() => handleRestoreSingleDispute(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              style={{ color: 'green' }}
            >
              Restore
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Permanently Delete"
            description="This action cannot be undone. Are you sure?"
            onConfirm={() => handlePermanentDeleteSingle(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              icon={<CloseCircleOutlined />}
              danger
            >
              Delete Permanently
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [leadDetailsCache, loadingLeadDetails, getStatusTag, handleViewDetails, handleOpenEditDrawer, handleRestoreSingleDispute, handlePermanentDeleteSingle, handleViewStatusHistory, handleViewFieldHistory]);

  // ========== ROW SELECTION ==========
  const rowSelection = {
    columnWidth: COLUMN_WIDTH.selection,
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.status === 'RESOLVED' || record.status === 'LOSS',
      name: record._id,
    }),
  };

  const deletedRowSelection = {
    columnWidth: COLUMN_WIDTH.selection,
    selectedRowKeys: deletedSelectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setDeletedSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // ========== WATCH STATUS FOR CONDITIONAL FIELDS ==========
  const watchStatus = Form.useWatch('status', form);
  const showResolutionFields = watchStatus === 'RESOLVED';

  const watchEditStatus = Form.useWatch('status', editForm);
  const showEditResolutionFields = watchEditStatus === 'RESOLVED';

  // ========== EFFECTS ==========
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Clear any queued lead search when the page goes away.
  useEffect(() => () => {
    if (leadSearchTimerRef.current) {
      clearTimeout(leadSearchTimerRef.current);
    }
  }, []);

  // Active-disputes list: re-fetches from the backend whenever the tab is active
  // and pagination, search, or the date filter changes — search/date/pagination
  // are sent as query params (buildListParams) instead of filtering a client-side
  // array, so every one of these changes needs a fresh request.
  useEffect(() => {
    if (activeTab !== 'active') return;
    fetchActiveDisputes();
  }, [activeTab, activePagination.current, activePagination.pageSize, searchText, quickFilter, filterDateRange, statusFilter, refreshNonce]);

  // Deleted-disputes list: same pattern as above.
  useEffect(() => {
    if (activeTab !== 'deleted') return;
    fetchDeletedDisputes();
  }, [activeTab, deletedPagination.current, deletedPagination.pageSize, searchText, quickFilter, filterDateRange, statusFilter, refreshNonce]);

  // ========== RENDER ==========
  return (
    <div ref={pageRef} style={{ padding: '24px', minHeight: '100vh', background: '#f5f5f5' }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Dispute Management</h2>
          <Space>
            {activeTab !== 'deleted' && selectedRowKeys.length > 0 && (
              <>
                <Button onClick={() => setSelectedRowKeys([])}>
                  Clear Selection ({selectedRowKeys.length})
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleSoftDelete}
                  loading={deleteLoading}
                >
                  Delete Selected ({selectedRowKeys.length})
                </Button>
              </>
            )}
            {activeTab === 'deleted' && deletedSelectedRowKeys.length > 0 && (
              <>
                <Button onClick={() => setDeletedSelectedRowKeys([])}>
                  Clear Selection ({deletedSelectedRowKeys.length})
                </Button>
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={handleRestoreDisputes}
                  loading={restoreLoading}
                  style={{ color: 'green' }}
                >
                  Restore Selected ({deletedSelectedRowKeys.length})
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handlePermanentDelete}
                  loading={permanentDeleteLoading}
                >
                  Permanently Delete ({deletedSelectedRowKeys.length})
                </Button>
              </>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={disputesLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenDrawer}
            >
              Create Dispute
            </Button>
          </Space>
        </div>

        {activeTab !== 'deleted' && selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', color: '#1890ff' }}>
            <CheckCircleOutlined style={{ marginRight: 8 }} />
            {selectedRowKeys.length} dispute(s) selected. Click "Delete Selected" to soft delete them.
          </div>
        )}

        {activeTab === 'deleted' && deletedSelectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff7e6', borderRadius: '4px', color: '#faad14' }}>
            <CheckCircleOutlined style={{ marginRight: 8 }} />
            {deletedSelectedRowKeys.length} dispute(s) selected in trash. Choose to restore or permanently delete.
          </div>
        )}

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Active Disputes" key="active">
            <SearchAndFilters
              localSearchValue={localSearchValue}
              onLocalSearchChange={setLocalSearchValue}
              onExecuteSearch={executeSearch}
              searchText={searchText}
              quickFilter={quickFilter}
              onQuickFilterChange={handleQuickFilterChange}
              showCustomRange={showCustomRange}
              filterDateRange={filterDateRange}
              onCustomRangeChange={handleCustomRangeChange}
              onResetFilters={resetFilters}
              onClearSearch={handleClearSearchFilter}
              onClearCustomDate={handleClearCustomDateFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
            />
            
            <div style={{ marginBottom: 12, color: '#666' }}>
              Showing {disputes.length} of {activePagination.total} dispute(s)
              {quickFilter === 'thisMonth' && (
                <Tag color="blue" style={{ marginLeft: 8 }} icon={<CalendarOutlined />}>
                  This Month's Disputes
                </Tag>
              )}
            </div>
            
            {disputesLoading && disputes.length === 0 ? (
              // True first load (nothing to show yet): skeleton rows instead of a
              // spinner blocking an empty table. Once there's data, a refetch
              // (pagination/search/filter change) keeps showing it and uses the
              // Table's own loading overlay below — no jarring skeleton flash on
              // every filter change.
              <div style={{ padding: '8px 0' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} active avatar paragraph={{ rows: 1, width: ['60%'] }} style={{ marginBottom: 20 }} />
                ))}
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={disputes}
                rowKey="_id"
                loading={disputesLoading}
                rowSelection={rowSelection}
                pagination={{
                  current: activePagination.current,
                  pageSize: activePagination.pageSize,
                  total: activePagination.total,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} disputes`,
                  showSizeChanger: true,
                    position: ['topRight'],
                  pageSizeOptions: ['12', '24', '48', '96'],
                  onChange: handleActivePaginationChange,
                  onShowSizeChange: handleActivePaginationChange,
                }}
                scroll={{ x: totalColumnWidth(columns) }}
                sticky={{ getContainer: getStickyContainer }}
              />
            )}
          </TabPane>
          
          <TabPane tab="Deleted Disputes" key="deleted">
            <SearchAndFilters
              localSearchValue={localSearchValue}
              onLocalSearchChange={setLocalSearchValue}
              onExecuteSearch={executeSearch}
              searchText={searchText}
              quickFilter={quickFilter}
              onQuickFilterChange={handleQuickFilterChange}
              showCustomRange={showCustomRange}
              filterDateRange={filterDateRange}
              onCustomRangeChange={handleCustomRangeChange}
              onResetFilters={resetFilters}
              onClearSearch={handleClearSearchFilter}
              onClearCustomDate={handleClearCustomDateFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
            />
            
            <div style={{ marginBottom: 12, color: '#666' }}>
              Showing {deletedDisputes.length} of {deletedPagination.total} deleted dispute(s)
              {quickFilter === 'thisMonth' && (
                <Tag color="blue" style={{ marginLeft: 8 }} icon={<CalendarOutlined />}>
                  This Month's Deleted Disputes
                </Tag>
              )}
            </div>
            
            {deletedDisputesLoading && deletedDisputes.length === 0 ? (
              <div style={{ padding: '8px 0' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} active avatar paragraph={{ rows: 1, width: ['60%'] }} style={{ marginBottom: 20 }} />
                ))}
              </div>
            ) : (
              <Table
                columns={deletedColumns}
                dataSource={deletedDisputes}
                rowKey="_id"
                loading={deletedDisputesLoading}
                rowSelection={deletedRowSelection}
                pagination={{
                  current: deletedPagination.current,
                  pageSize: deletedPagination.pageSize,
                  total: deletedPagination.total,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} deleted disputes`,
                  showSizeChanger: true,
                    position: ['topRight'],
                  pageSizeOptions: ['12', '24', '48', '96'],
                  onChange: handleDeletedPaginationChange,
                  onShowSizeChange: handleDeletedPaginationChange,
                }}
                scroll={{ x: totalColumnWidth(deletedColumns) }}
                sticky={{ getContainer: getStickyContainer }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Create Dispute Drawer */}
      <Drawer
        title="Create New Dispute"
        placement="right"
        width={600}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        extra={
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleCloseDrawer}
          />
        }
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={handleCloseDrawer}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
              disabled={duplicateCheck?.hasDuplicate}
            >
              Submit
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="lead_id" label="Select Lead" rules={[{ required: true, message: 'Please select a lead' }]}>
                <Select
                  placeholder="Search and select lead"
                  showSearch
                  loading={leadLoading}
                  onSearch={handleLeadSearch}
                  onChange={handleLeadSelect}
                  filterOption={false}
                  options={leadOptions}
                  optionFilterProp="label"
                  notFoundContent={leadLoading ? <Spin size="small" /> : 'No leads found'}
                />
              </Form.Item>
              {checkingDuplicate && (
                <div style={{ marginTop: -16, marginBottom: 16, color: '#999', fontSize: 12 }}>
                  <Spin size="small" style={{ marginRight: 6 }} />
                  Checking for an existing dispute on this lead…
                </div>
              )}
              {duplicateCheck?.hasDuplicate && (
                <Alert
                  style={{ marginTop: -16, marginBottom: 16 }}
                  type="warning"
                  showIcon
                  message="This lead already has an active dispute"
                  description={`Status: ${duplicateCheck.data?.status || 'N/A'}${duplicateCheck.data?.dispute_reason ? ` — "${duplicateCheck.data.dispute_reason}"` : ''}. Resolve or close it before opening a new one.`}
                />
              )}
            </Col>

            <Col span={24}>
              <Form.Item name="dispute_reason" label="Dispute Reason" tooltip="Enter the reason for dispute (free text)" rules={[{ required: true, message: 'Please enter a dispute reason' }]}>
                <Input.TextArea rows={3} placeholder="Enter dispute reason..." maxLength={200} showCount />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="description" label="Description" tooltip="Additional details about the dispute">
                <TextArea rows={4} placeholder="Enter description (optional)..." maxLength={500} showCount />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="OPEN">
                <Select placeholder="Select status">
                  <Option value="OPEN">Open</Option>
                  <Option value="UNDER_REVIEW">Under Review</Option>
                  <Option value="RESOLVED">Resolved</Option>
                  <Option value="LOSS">Loss</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                initialValue="MEDIUM"
                tooltip="SLA deadline is set automatically from priority if not specified (Low: 72h, Medium: 48h, High: 24h, Critical: 12h)."
              >
                <Select placeholder="Select priority" allowClear>
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                  <Option value="CRITICAL">Critical</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="dispute_date"
                label="Dispute Created Date (PST)"
                tooltip="The date the dispute was raised, in Pacific Time. Defaults to now."
                rules={[{ required: true, message: 'Please select the dispute created date' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format={PST_FORMAT}
                  placeholder="Select dispute created date"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="sla_deadline" label="SLA Deadline (PST)" tooltip="Default: 48 hours from creation if not specified">
                <DatePicker showTime={{ format: 'HH:mm' }} format={PST_FORMAT} placeholder="Select SLA deadline (optional)" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            {showResolutionFields && (
              <Col span={24}>
                <Form.Item name="resolution_type" label="Resolution Type" tooltip="Required when creating a dispute as Resolved" rules={[{ required: true, message: 'Please select resolution type' }]}>
                  <Select placeholder="Select resolution type">
                    <Option value="WIN">Win</Option>
                    <Option value="LOSS">Loss</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Drawer>

      {/* Edit Dispute Drawer */}
      <Drawer
        title="Edit Dispute"
        placement="right"
        width={600}
        onClose={handleCloseEditDrawer}
        open={editDrawerVisible}
        extra={
          <Button type="text" icon={<CloseOutlined />} onClick={handleCloseEditDrawer} />
        }
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={handleCloseEditDrawer}>Cancel</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => editForm.submit()} loading={editLoading}>
              Update
            </Button>
          </Space>
        }
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="dispute_reason" label="Dispute Reason" rules={[{ required: true, message: 'Please select dispute reason' }]}>
                <Input.TextArea rows={3} placeholder="Enter dispute reason..." maxLength={200} showCount />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter description' }]}>
                <TextArea rows={4} placeholder="Describe the reason for this dispute..." maxLength={500} showCount />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select status">
                  <Option value="OPEN">Open</Option>
                  <Option value="UNDER_REVIEW">Under Review</Option>
                  <Option value="RESOLVED">Resolved</Option>
                  <Option value="LOSS">Loss</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select placeholder="Select priority">
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                  <Option value="CRITICAL">Critical</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="dispute_date"
                label="Dispute Created Date (PST)"
                tooltip="The date the dispute was raised, in Pacific Time. Every change is kept in this field's history."
                rules={[{ required: true, message: 'Please select the dispute created date' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format={PST_FORMAT}
                  placeholder="Select dispute created date"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="sla_deadline" label="SLA Deadline (PST)" tooltip="Default: 48 hours from creation">
                <DatePicker showTime={{ format: 'HH:mm' }} format={PST_FORMAT} placeholder="Select SLA deadline" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            {showEditResolutionFields && (
              <>
                <Col span={24}>
                  <Form.Item name="resolution_type" label="Resolution Type" rules={[{ required: true, message: 'Please select resolution type' }]}>
                    <Select placeholder="Select resolution type">
                      <Option value="WIN">Win</Option>
                      <Option value="LOSS">Loss</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="resolution_notes" label="Resolution Notes" rules={[{ required: true, message: 'Please enter resolution notes' }]}>
                    <TextArea rows={3} placeholder="Explain the resolution..." maxLength={500} showCount />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="resolved_at" label="Resolved At (PST)">
                    <DatePicker showTime={{ format: 'HH:mm' }} format={PST_FORMAT} placeholder="Select resolution date" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Drawer>

      {/* Dispute Details Modal */}
      <Modal
        title="Dispute Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>Close</Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => {
            setDetailModalVisible(false);
            handleOpenEditDrawer(selectedDispute);
          }}>
            Edit Dispute
          </Button>
        ]}
        width={1000}
      >
        {selectedDispute && (() => {
          const leadId = selectedDispute.lead_id?._id || selectedDispute.lead_id;
          const leadDetails = leadDetailsCache[leadId] || selectedDispute.leadDetails;
          
          const formatCurrency = (amount) => {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2
            }).format(amount || 0);
          };
          
          return (
            <>
              <Descriptions bordered column={2} title="📋 Personal Information" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Full Name" span={2}>
                  <strong style={{ fontSize: 16, color: '#1890ff' }}>
                    {leadDetails?.fullName || selectedDispute.lead_id?.personalInfo?.fullName || 'N/A'}
                  </strong>
                </Descriptions.Item>
                <Descriptions.Item label="Email">{leadDetails?.email || selectedDispute.lead_id?.personalInfo?.email || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Phone Number">{leadDetails?.phoneNumber || selectedDispute.lead_id?.personalInfo?.phoneNumber || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Zip Code">{leadDetails?.zipCode || selectedDispute.lead_id?.personalInfo?.zipCode || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Time Zone">{leadDetails?.customerTimeZone || selectedDispute.lead_id?.personalInfo?.customerTimeZone || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Order Number"><Tag color="blue">{leadDetails?.orderNumber || selectedDispute.lead_id?.orderNumber || 'N/A'}</Tag></Descriptions.Item>
                <Descriptions.Item label="Lead Stage"><Tag color="purple">{leadDetails?.leadStage || selectedDispute.lead_id?.leadStage || 'N/A'}</Tag></Descriptions.Item>
                <Descriptions.Item label="Current Agent"><Tag color="geekblue">{leadDetails?.currentAgentName || selectedDispute.lead_id?.handledBy?.name || 'N/A'}</Tag></Descriptions.Item>
              </Descriptions>

              <Descriptions bordered column={2} title="🚗 Vehicle Information" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Make">{leadDetails?.vehicleInfo?.make || selectedDispute.lead_id?.vehicleInfo?.make || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Model">{leadDetails?.vehicleInfo?.model || selectedDispute.lead_id?.vehicleInfo?.model || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Year"><Tag color="blue">{leadDetails?.vehicleInfo?.year || selectedDispute.lead_id?.vehicleInfo?.year || 'N/A'}</Tag></Descriptions.Item>
                <Descriptions.Item label="Part"><Tag color="cyan">{leadDetails?.vehicleInfo?.part || selectedDispute.lead_id?.vehicleInfo?.part || 'N/A'}</Tag></Descriptions.Item>
                <Descriptions.Item label="VIN" span={2}><code>{leadDetails?.vehicleInfo?.vin || selectedDispute.lead_id?.vehicleInfo?.vin || 'N/A'}</code></Descriptions.Item>
              </Descriptions>

              {(leadDetails?.saleMadeCharge?.customerAmountReceived > 0 || 
                leadDetails?.saleMadeCharge?.yardPrice > 0 ||
                leadDetails?.saleMadeCharge?.customerCCFee > 0 ||
                leadDetails?.saleMadeCharge?.totalGP !== 0) && (
                <Descriptions bordered column={2} title="💰 Sale Made Charge" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Customer Amount Received">
                    <strong style={{ color: '#52c41a', fontSize: 15 }}>
                      {formatCurrency(leadDetails?.saleMadeCharge?.customerAmountReceived || selectedDispute.lead_id?.saleMadeCharge?.customerAmountReceived || 0)}
                    </strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Yard Price">
                    <strong style={{ color: '#fa8c16', fontSize: 15 }}>
                      {formatCurrency(leadDetails?.saleMadeCharge?.yardPrice || selectedDispute.lead_id?.saleMadeCharge?.yardPrice || 0)}
                    </strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Customer CC Fee">
                    {formatCurrency(leadDetails?.saleMadeCharge?.customerCCFee || selectedDispute.lead_id?.saleMadeCharge?.customerCCFee || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Gross Profit (GP)">
                    <strong style={{ 
                      color: (leadDetails?.saleMadeCharge?.totalGP || selectedDispute.lead_id?.saleMadeCharge?.totalGP || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                      fontSize: 16
                    }}>
                      {formatCurrency(leadDetails?.saleMadeCharge?.totalGP || selectedDispute.lead_id?.saleMadeCharge?.totalGP || 0)}
                    </strong>
                  </Descriptions.Item>
                </Descriptions>
              )}

              <Descriptions bordered column={2} title="⚠️ Dispute Information" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Dispute Reason" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: '#fff7e6', padding: '8px 12px', borderRadius: '6px', borderLeft: '3px solid #faad14' }}>
                    {selectedDispute.dispute_reason || 'N/A'}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap', maxHeight: '150px', overflowY: 'auto', backgroundColor: '#fafafa', padding: '8px 12px', borderRadius: '6px' }}>
                    {selectedDispute.description || 'No description provided'}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Status">{getStatusTag(selectedDispute.status)}</Descriptions.Item>
                <Descriptions.Item label="Priority">{getPriorityTag(selectedDispute.priority)}</Descriptions.Item>
                <Descriptions.Item label="Dispute Created Date (PST)">
                  {formatPST(getDisputeDate(selectedDispute), PST_FORMAT_SECONDS)}
                </Descriptions.Item>
                <Descriptions.Item label="SLA Deadline (PST)">
                  {selectedDispute.sla_deadline ? formatPST(selectedDispute.sla_deadline, PST_FORMAT_SECONDS) : '48 hours (Default)'}
                </Descriptions.Item>
                {selectedDispute.status === 'RESOLVED' && (
                  <>
                    <Descriptions.Item label="Resolution Type" span={2}><Tag color="green">{selectedDispute.resolution_type?.replace(/_/g, ' ') || 'N/A'}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Resolution Notes" span={2}>
                      <div style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f6ffed', padding: '8px 12px', borderRadius: '6px', borderLeft: '3px solid #52c41a' }}>
                        {selectedDispute.resolution_notes || 'No resolution notes provided'}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Resolved At (PST)">{formatPST(selectedDispute.resolved_at, PST_FORMAT_SECONDS)}</Descriptions.Item>
                  </>
                )}
                {selectedDispute.status === 'LOSS' && (
                  <>
                    <Descriptions.Item label="Loss Type" span={2}><Tag color="red">{selectedDispute.resolution_type?.replace(/_/g, ' ') || 'N/A'}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Loss Notes" span={2}>
                      <div style={{ whiteSpace: 'pre-wrap', backgroundColor: '#fff1f0', padding: '8px 12px', borderRadius: '6px', borderLeft: '3px solid #ff4d4f' }}>
                        {selectedDispute.resolution_notes || 'No loss notes provided'}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Loss Recorded At (PST)">{formatPST(selectedDispute.resolved_at, PST_FORMAT_SECONDS)}</Descriptions.Item>
                  </>
                )}
              </Descriptions>

              {(leadDetails?.shippingAddress?.address || leadDetails?.billingAddress?.address) && (
                <Descriptions bordered column={2} title="📍 Address Information" style={{ marginBottom: 16 }}>
                  {leadDetails?.shippingAddress?.address && (
                    <Descriptions.Item label="Shipping Address" span={2}>
                      {leadDetails.shippingAddress.address}
                      {leadDetails.shippingAddress.city && `, ${leadDetails.shippingAddress.city}`}
                      {leadDetails.shippingAddress.state && `, ${leadDetails.shippingAddress.state}`}
                      {leadDetails.shippingAddress.zipCode && ` ${leadDetails.shippingAddress.zipCode}`}
                    </Descriptions.Item>
                  )}
                  {leadDetails?.billingAddress?.address && (
                    <Descriptions.Item label="Billing Address" span={2}>
                      {leadDetails.billingAddress.address}
                      {leadDetails.billingAddress.city && `, ${leadDetails.billingAddress.city}`}
                      {leadDetails.billingAddress.state && `, ${leadDetails.billingAddress.state}`}
                      {leadDetails.billingAddress.zipCode && ` ${leadDetails.billingAddress.zipCode}`}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}

              <Descriptions bordered column={2} title="ℹ️ System Information" style={{ marginTop: 16 }}>
                <Descriptions.Item label="Created By">
                  {selectedDispute.disputed_by_user_id?.name || selectedDispute.disputed_by_user_id?.email || selectedDispute.created_by || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated (PST)">
                  {formatPST(selectedDispute.updated_at || selectedDispute.updatedAt, PST_FORMAT_SECONDS)}
                </Descriptions.Item>
              </Descriptions>
            </>
          );
        })()}
      </Modal>

      <StatusHistoryModal
        visible={statusHistoryModalVisible}
        onClose={handleCloseStatusHistoryModal}
        loading={statusHistoryLoading}
        history={statusHistoryData}
        record={statusHistoryRecord}
      />

      <FieldHistoryModal
        visible={!!fieldHistoryField}
        field={fieldHistoryField}
        onClose={handleCloseFieldHistoryModal}
        loading={fieldHistoryLoading}
        history={fieldHistoryData}
        record={fieldHistoryRecord}
      />
    </div>
  );
};

const DisputeLeadWithErrorBoundary = () => (
  <ErrorBoundary message="The Dispute Lead page hit an unexpected error and could not render.">
    <DisputeLead />
  </ErrorBoundary>
);

export default DisputeLeadWithErrorBoundary;
